/* eslint-disable react/prop-types */
/* eslint-disable no-underscore-dangle */
import { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import Dropdown from "@shared/v2/Dropdown";

import FieldPhoneV1 from "../../../shared/UtilsV1/FieldPhoneV1";
import FieldLabelV1 from "../../../shared/UtilsV1/FieldLabelV1";
import Radio from "../../../shared/UtilsV1/FieldRadioV1/Radio";
import RemoveFieldButton from "../../../shared/v2/RemoveFieldButton";
import PhoneNumberModal from "../../PhoneNumberModal";
import { useContactPerson } from "../../../reducers/contactsReducer/selectors";
import { setShowRemoveRoutingWarning } from "../../../reducers/contactsReducer";

function PhoneView({
  removable,
  currentPhone,
  index,
  required,
  phoneList,
  setPersonValue,
  options,
  modalType,
}) {
  const [showContext, setShowContext] = useState(null);
  const { category, value: currentPhoneNumber } = currentPhone;
  const isPrimary = currentPhone.position === 1;
  const person = useContactPerson();
  const dispatch = useDispatch();

  const websiteDisplayPhones = person.websiteDisplayPhoneDetailsAttributes || [];
  const isAdding = !person.id;

  const updateWebsiteDisplayPhones = (newPhoneList) => {
    if (modalType === "user" && isAdding) {
      const websiteDisplayPhoneDetailsAttributes = newPhoneList
        .filter((p) => p.value && !p._destroy && /work|mobile/.test(p.category))
        .map((phone) => ({
          name: "website_display_phone",
          category: phone.category === "work" ? "office" : "mobile",
          value: phone.value,
          position: phone.category === "mobile" ? 1 : 2,
          tempId: Date.now(),
        }));
      setPersonValue({
        key: "websiteDisplayPhoneDetailsAttributes",
        value: websiteDisplayPhoneDetailsAttributes,
      });
    }
  };

  const onPrimaryChange = (phonePosition) => {
    const primaryIdx = phoneList.findIndex((phone) => phone.position === phonePosition);

    const updatedPhoneList = phoneList.map((phone, idx) =>
      idx === primaryIdx
        ? { ...phone, position: 1 }
        : { ...phone, position: idx > primaryIdx ? idx + 1 : idx + 2 },
    );

    setPersonValue({ key: "phoneDetailsAttributes", value: updatedPhoneList });
  };

  const onRemove = (targetPhone) => {
    const filteredPhoneList = phoneList
      .map((phone) => {
        if (phone === targetPhone) return { ...phone, value: "", _destroy: true };
        return { ...phone };
      })
      .filter((phone) => !phone._destroy || phone.id);

    if (targetPhone.position === 1 && filteredPhoneList.length > 0) {
      filteredPhoneList[0].position = 1;
    }

    setPersonValue({ key: "phoneDetailsAttributes", value: filteredPhoneList });
    updateWebsiteDisplayPhones(filteredPhoneList);
  };

  const updatePhone = (targetPhone, value) => {
    const newPhoneList = phoneList.map((phone) => {
      if (phone !== targetPhone) return phone;

      return { ...phone, ...value };
    });

    setPersonValue({
      key: "phoneDetailsAttributes",
      value: newPhoneList,
    });

    updateWebsiteDisplayPhones(newPhoneList);

    if (isAdding) return;

    const targetPhoneVal = targetPhone.value;
    const isCategory = value.category;

    const officePhone = websiteDisplayPhones.find(
      (phone) => phone.category === "office" && phone.value === targetPhoneVal,
    );
    if (officePhone) {
      const updatedPhones = websiteDisplayPhones.map((phone) => {
        if (phone.category === "office" && phone.value === targetPhoneVal) {
          return { ...phone, value: isCategory ? phone.value : value.value };
        }
        return phone;
      });

      setPersonValue({
        key: "websiteDisplayPhoneDetailsAttributes",
        value: updatedPhones,
      });
    }

    const mobilePhone = websiteDisplayPhones.find(
      (phone) => phone.category === "mobile" && phone.value === targetPhoneVal,
    );
    if (mobilePhone) {
      const updatedPhones = websiteDisplayPhones.map((phone) => {
        if (phone.category === "mobile" && phone.value === targetPhoneVal) {
          return { ...phone, value: isCategory ? phone.value : value.value };
        }
        return phone;
      });

      setPersonValue({
        key: "websiteDisplayPhoneDetailsAttributes",
        value: updatedPhones,
      });
    }
  };

  return currentPhone?._destroy ? null : (
    <div className="tw-flex">
      <div className="tw-flex tw-gap-[24px] tw-items-center tw-flex-auto">
        <FieldPhoneV1
          label="Phone"
          value={currentPhoneNumber}
          onChange={(value) => updatePhone(currentPhone, { value })}
          required={required && index === 0}
        />
        <div className="tw-flex tw-gap-[8px] tw-flex-1">
          <Dropdown
            containerClassName="tw-w-full"
            label="Phone Type"
            placeholder="Choose One"
            options={options}
            value={options.find((o) => o.value === category)}
            onChange={(o) => updatePhone(currentPhone, { category: o.value })}
            menuShouldComeToFront
          />

          {removable && (
            <RemoveFieldButton
              onClick={() => {
                if (currentPhone.archiveConversationsCount > 0) {
                  setShowContext({
                    changedNumbers: [{ previousNumber: currentPhoneNumber }],
                    conversationCount: currentPhone.archiveConversationsCount,
                  });
                } else {
                  if (
                    person.loginUserAttributes?.routingNumberCanonical === currentPhone.canonicalValue &&
                    !isAdding
                  ) {
                    dispatch(
                      setShowRemoveRoutingWarning({
                        phoneNumber: currentPhone.canonicalValue,
                        onSubmit: () => onRemove(currentPhone),
                      }),
                    );
                    return;
                  }

                  onRemove(currentPhone);
                }
              }}
              className="tw-mb-1px tw-mt-auto"
            />
          )}
        </div>
      </div>

      <div>
        <FieldLabelV1
          label="Primary"
          toolTipInfoContent={
            <div className="tw-w-[180px] tw-text-left">
              Your primary number is used to receive communication when sent from other users through the CRM.
              Please ensure your primary number can send and receive text messages.
            </div>
          }
          toolTipPlacement="top"
        />
        <div
          aria-label="primary"
          className="tw-flex tw-justify-center tw-items-center tw-py-4"
          onClick={() => onPrimaryChange(currentPhone.position)}
          onKeyDown={() => onPrimaryChange(currentPhone.position)}
          role="button"
          tabIndex="0"
        >
          <Radio checked={isPrimary} />
        </div>
      </div>
      <PhoneNumberModal
        showContext={showContext}
        onClose={(phoneRemovalAck) => {
          if (phoneRemovalAck) onRemove(currentPhone);
          setShowContext(null);
        }}
      />
    </div>
  );
}
const phonePropTypeShape = PropTypes.shape({
  category: PropTypes.string,
  id: PropTypes.number,
  name: PropTypes.string,
  position: PropTypes.number,
  value: PropTypes.string,
  _destroy: PropTypes.bool,
  archiveConversationsCount: PropTypes.number,
});
PhoneView.propTypes = {
  removable: PropTypes.bool,
  currentPhone: phonePropTypeShape,
  modalType: PropTypes.string.isRequired,
  index: PropTypes.number,
  phoneList: PropTypes.arrayOf(phonePropTypeShape),
  required: PropTypes.bool,
  setPersonValue: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
};

PhoneView.defaultProps = {
  removable: false,
  currentPhone: null,
  index: null,
  required: false,
  phoneList: [],
  options: [],
};

export default PhoneView;
